/*
 * chatStream — Consume el endpoint SSE `/api/chat/{match_id}/stream`.
 *
 * Uso:
 *   const controller = new AbortController();
 *   chatStream({
 *     matchId,
 *     text: "Hola",
 *     onChunk: (piece) => setPartial((p) => p + piece),
 *     onDone: (full) => setPartial(full),
 *     onError: (e) => toast.error(e),
 *     signal: controller.signal,
 *   });
 *
 * Nota: usamos fetch + ReadableStream (no EventSource) porque EventSource sólo
 * soporta GET y necesitamos POST con body JSON.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

import { devWarn } from "./devLog";

export async function chatStream({ matchId, text, onChunk, onDone, onError, signal }) {
  try {
    const res = await fetch(`${BACKEND_URL}/api/chat/${matchId}/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
      signal,
    });

    if (!res.ok || !res.body) {
      const err = await res.text().catch(() => "network");
      onError?.(err || `HTTP ${res.status}`);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let fullText = "";

    // Read the stream chunk by chunk
    // Each SSE frame ends with '\n\n' and begins with 'data: '
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let idx;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);

        for (const line of frame.split("\n")) {
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            const evt = JSON.parse(payload);
            if (evt.type === "chunk") {
              fullText += evt.text;
              onChunk?.(evt.text);
            } else if (evt.type === "done") {
              onDone?.(evt.full || fullText);
              return;
            } else if (evt.type === "error") {
              onError?.(evt.detail || "AI error");
              return;
            }
          } catch (parseErr) {
            // Ignore malformed SSE frames (partial payloads while chunks arrive).
            // Only log in dev if it's not the expected mid-stream truncation.
            if (payload.length > 0 && payload[0] === "{") {
              devWarn("[chatStream] malformed frame:", payload.slice(0, 80), parseErr?.message);
            }
          }
        }
      }
    }
    // Stream ended without an explicit 'done' — finalize with what we have
    onDone?.(fullText);
  } catch (e) {
    if (e?.name === "AbortError") return;
    onError?.(e?.message || String(e));
  }
}
